import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { Math as MathFormula } from "@/components/Math";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";

export default function PermutationCombinationCalculator() {
  const [n, setN] = useState("");
  const [r, setR] = useState("");
  const [result, setResult] = useState<{
    permutation: number;
    combination: number;
  } | null>(null);

  const factorial = (num: number): number => {
    if (num <= 1) return 1;
    return num * factorial(num - 1);
  };

  const calculate = () => {
    const nNum = parseInt(n);
    const rNum = parseInt(r);

    if (isNaN(nNum) || isNaN(rNum) || nNum < 0 || rNum < 0) {
      toast({
        title: "Invalid Input",
        description: "Please enter valid non-negative integers.",
        variant: "destructive",
      });
      return;
    }

    if (rNum > nNum) {
      toast({
        title: "Invalid Input",
        description: "r cannot be greater than n.",
        variant: "destructive",
      });
      return;
    }

    // Permutation: P(n,r) = n! / (n-r)!
    const permutation = factorial(nNum) / factorial(nNum - rNum);

    // Combination: C(n,r) = n! / (r! * (n-r)!)
    const combination = factorial(nNum) / (factorial(rNum) * factorial(nNum - rNum));

    setResult({ permutation, combination });

    toast({
      title: "Calculation Complete",
      description: `P(${nNum},${rNum}) and C(${nNum},${rNum}) calculated`,
    });
  };

  const reset = () => {
    setN("");
    setR("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Permutation & Combination Calculator" 
      calculatorPath="/calculator/permutation-combination"
      category="math"
      subcategory="Statistics"
    >
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Permutation & Combination Calculator</CardTitle>
            <CardDescription>
              Calculate arrangements with and without order
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="n">n (total items)</Label>
                <Input
                  id="n"
                  type="number"
                  step="1"
                  value={n}
                  onChange={(e) => setN(e.target.value)}
                  placeholder="Total number of items"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="r">r (items to choose)</Label>
                <Input
                  id="r"
                  type="number"
                  step="1"
                  value={r}
                  onChange={(e) => setR(e.target.value)}
                  placeholder="Number of items to select"
                />
              </div>
            </div>

            <div className="flex gap-4">
              <Button onClick={calculate} className="flex-1">Calculate</Button>
              <Button onClick={reset} variant="outline" className="flex-1">Reset</Button>
            </div>

            {result && (
              <>
                <div className="space-y-4">
                <Card className="bg-muted/50">
                  <CardHeader>
                    <CardTitle className="text-lg">Permutation (Order Matters)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold">
                      {result.permutation.toLocaleString()}
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      P({n},{r}) = {result.permutation.toLocaleString()} ways
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-muted/50">
                  <CardHeader>
                    <CardTitle className="text-lg">Combination (Order Does Not Matter)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold">
                      {result.combination.toLocaleString()}
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      C({n},{r}) = {result.combination.toLocaleString()} ways
                    </p>
                  </CardContent>
                </Card>
              </div>

              <CalculatorSuperCalcCTA config={calculatorCTAs["permutation-combination"]} trigger={result?.permutation} />
              </>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-sm">Formulas</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm font-semibold mb-2">Permutation (P)</p>
                  <MathFormula block>{String.raw`P(n,r) = \frac{n!}{(n-r)!}`}</MathFormula>
                </div>
                <div>
                  <p className="text-sm font-semibold mb-2">Combination (C)</p>
                  <MathFormula block>{String.raw`C(n,r) = \frac{n!}{r!(n-r)!}`}</MathFormula>
                </div>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
